/**
 * LessonOverview - 레슨 개요 컴포넌트
 *
 * 새 학습 페이지에서 레슨 정보와 컴포넌트(워밍업/영상/퀴즈) 상태를 표시.
 * LessonDetailsResponse API 데이터를 기반으로 렌더링.
 */

import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
    Play,
    CheckCircle,
    BookOpen,
    Dumbbell,
    MonitorPlay,
    ClipboardCheck,
    Lock,
    AlertCircle,
} from 'lucide-react'
import type {
    LessonDetailsResponse,
    ComponentDetail,
} from '@/api/learningSession'

// Progress status colors
const STATUS_COLORS = {
    not_started: {
        bg: 'bg-gray-100',
        text: 'text-gray-500',
        icon: 'text-gray-400',
        border: 'border-gray-200',
    },
    in_progress: {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        icon: 'text-blue-500',
        border: 'border-blue-200',
    },
    completed: {
        bg: 'bg-green-50',
        text: 'text-green-600',
        icon: 'text-green-500',
        border: 'border-green-200',
    },
} as const

// Component type icon/color configurations (titles/descriptions use i18n)
const COMPONENT_ICON_CONFIG = {
    quiz: {
        pre: { icon: Dumbbell, colorClass: 'indigo' },
        post: { icon: ClipboardCheck, colorClass: 'green' },
        wrapup: { icon: ClipboardCheck, colorClass: 'amber' },
        checkup: { icon: ClipboardCheck, colorClass: 'purple' },
    },
    video: {
        main: { icon: MonitorPlay, colorClass: 'blue' },
    },
    assignment: {
        main: { icon: BookOpen, colorClass: 'emerald' },
    },
} as const

type ComponentConfigItem = {
    icon: typeof Play
    titleKey: string
    descriptionKey: string
    colorClass: string
}

function getComponentConfig(component: ComponentDetail): ComponentConfigItem | null {
    const titleKeys: Record<string, Record<string, string>> = {
        quiz: {
            pre: 'learning.lessonOverview.warmup',
            post: 'learning.lessonOverview.reviewQuiz',
            wrapup: 'learning.lessonOverview.wrapupQuiz',
            checkup: 'learning.lessonOverview.checkupQuiz',
        },
        video: { main: 'learning.lessonOverview.videoLecture' },
        assignment: { main: 'learning.lessonOverview.diary' },
    }
    const descKeys: Record<string, Record<string, string>> = {
        quiz: {
            pre: 'learning.lessonOverview.warmupDesc',
            post: 'learning.lessonOverview.reviewQuizDesc',
            wrapup: 'learning.lessonOverview.wrapupQuizDesc',
            checkup: 'learning.lessonOverview.checkupQuizDesc',
        },
        video: { main: 'learning.lessonOverview.videoLectureDesc' },
        assignment: { main: 'learning.lessonOverview.diaryDesc' },
    }

    const location = component.type === 'quiz' ? component.location : 'main'
    const iconConfig = component.type === 'video'
        ? COMPONENT_ICON_CONFIG.video.main
        : component.type === 'assignment'
            ? COMPONENT_ICON_CONFIG.assignment.main
            : component.type === 'quiz'
                ? COMPONENT_ICON_CONFIG.quiz[component.location as keyof typeof COMPONENT_ICON_CONFIG.quiz]
                : null

    if (!iconConfig) return null

    const titleKey = titleKeys[component.type]?.[location]
    const descKey = descKeys[component.type]?.[location]
    if (!titleKey || !descKey) return null

    return {
        icon: iconConfig.icon,
        titleKey,
        descriptionKey: descKey,
        colorClass: iconConfig.colorClass,
    }
}

function getStatusLabelKey(status: string): string {
    switch (status) {
        case 'not_started': return 'learning.lessonOverview.notStarted'
        case 'in_progress': return 'learning.lessonOverview.inProgress'
        case 'completed': return 'learning.lessonOverview.completed'
        default: return status
    }
}

function formatProgressText(component: ComponentDetail, t: (key: string, options?: Record<string, unknown>) => string): string | null {
    const { progress, type } = component
    if (!progress) return null

    if (type === 'video' && progress.progressPercent != null) {
        return t('learning.lessonOverview.watchedPercent', { percent: progress.progressPercent })
    }

    if (type === 'quiz') {
        if (progress.bestScore != null) {
            return t('learning.lessonOverview.bestScore', { score: progress.bestScore })
        }
        if (progress.attemptCount != null && progress.attemptCount > 0) {
            return t('learning.lessonOverview.attemptCount', { count: progress.attemptCount })
        }
    }

    return null
}

export interface LessonOverviewProps {
    /** 레슨 상세 데이터 */
    lessonDetails: LessonDetailsResponse
    /** 썸네일 URL */
    thumbnailUrl?: string | null
    /** 시작 버튼 클릭 핸들러 */
    onStart: () => void
    /** 특정 컴포넌트 시작 핸들러 */
    onStartComponent?: (type: string, location: string) => void
    /** 로딩 상태 */
    isLoading?: boolean
    /** 순차 모드 여부 (잠금 표시용) */
    isSequentialMode?: boolean
    /** Pre-AIQ 완료해야 학습 가능한지 여부 */
    canStartLearning?: boolean
    /** AIQ 페이지로 이동 핸들러 (Pre-AIQ 안내용) */
    onNavigateToAiq?: () => void
}

export function LessonOverview({
    lessonDetails,
    thumbnailUrl,
    onStart,
    onStartComponent,
    isLoading = false,
    isSequentialMode = false,
    canStartLearning = true,
    onNavigateToAiq,
}: LessonOverviewProps) {
    const { t } = useTranslation('learner')
    const {
        title: displayTitle,
        components,
        overallStatus,
        overallProgressPercent,
    } = lessonDetails

    const hasStartedLearning = overallStatus !== 'not_started'
    const statusColors = STATUS_COLORS[overallStatus as keyof typeof STATUS_COLORS] || STATUS_COLORS.not_started

    // 컴포넌트 순서 결정 (enabled된 것만)
    const enabledComponents = components.filter(c => c.enabled)

    // 현재 진행해야 할 컴포넌트 인덱스 계산
    const currentComponentIndex = enabledComponents.findIndex(
        c => !c.progress || c.progress.status !== 'completed'
    )

    const isComponentLocked = (index: number): boolean => {
        if (!isSequentialMode) return false
        // 순차 모드에서는 이전 컴포넌트가 완료되어야 다음 접근 가능
        return index > 0 && currentComponentIndex >= 0 && index > currentComponentIndex
    }

    return (
        <div className="space-y-8 duration-300 animate-in fade-in">
            {/* Top Section: Status Badge */}
            <div className="flex gap-3 items-center">
                <span className={cn(
                    "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium",
                    statusColors.bg,
                    statusColors.text
                )}>
                    {t(getStatusLabelKey(overallStatus))}
                </span>
                {overallProgressPercent > 0 && (
                    <span className="text-sm text-gray-500">
                        {t('learning.lessonOverview.percentComplete', { percent: overallProgressPercent })}
                    </span>
                )}
            </div>

            {/* Title */}
            <h1 className="flex gap-3 items-center text-2xl font-bold leading-tight text-gray-900 md:text-3xl">
                <BookOpen className="w-7 h-7 text-gray-700 md:w-8 md:h-8" />
                <ContentMetadataText text={displayTitle} />
            </h1>

            {/* Two-Column Layout */}
            <div className="flex flex-col gap-6 md:flex-row">
                {/* Left Column: Thumbnail (60-65% width) */}
                <div className="w-full md:w-3/5">
                    <div className="flex overflow-hidden justify-center items-center w-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl border border-gray-200 shadow-md aspect-video">
                        {thumbnailUrl ? (
                            <img
                                src={thumbnailUrl}
                                alt={displayTitle}
                                className="object-cover w-full h-full"
                            />
                        ) : (
                            <div className="flex flex-col justify-center items-center text-gray-400">
                                <MonitorPlay className="mb-2 w-16 h-16" />
                                <span className="text-sm">{t('learning.lessonOverview.videoThumbnail')}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column: 학습 목표 + Start Button (35-40% width) */}
                <div className="flex flex-col gap-4 w-full md:w-2/5">
                    {/* 학습 목표 Card */}
                    <div className="flex-1 p-6 bg-slate-100 rounded-2xl">
                        <div className="flex gap-2 items-center mb-3">
                            <BookOpen className="w-5 h-5 text-gray-700" />
                            <h3 className="text-lg font-bold text-gray-900">{t('learning.lessonOverview.learningObjective')}</h3>
                        </div>
                        <p className="text-base leading-relaxed text-gray-700 whitespace-pre-line">
                            <ContentMetadataText text={lessonDetails.queturePoint || t('learning.lessonOverview.defaultObjective')} />
                        </p>
                    </div>

                    {/* Start Button or Pre-AIQ Warning */}
                    {canStartLearning === false ? (
                        // Pre-AIQ 미완료 안내
                        <div className="flex flex-col gap-3 items-center p-4 bg-amber-50 rounded-xl border border-amber-200">
                            <div className="flex gap-2 items-center text-amber-700">
                                <AlertCircle className="w-5 h-5" />
                                <span className="font-medium">{t('learning.lessonOverview.preAiqRequired')}</span>
                            </div>
                            <p className="text-sm text-center text-amber-600">
                                {t('learning.lessonOverview.preAiqRequiredDesc')}
                            </p>
                            {onNavigateToAiq && (
                                <button
                                    onClick={onNavigateToAiq}
                                    className="px-6 py-2 font-medium text-white bg-amber-600 rounded-lg transition-colors hover:bg-amber-700"
                                >
                                    {t('learning.lessonOverview.goToAiqTest')}
                                </button>
                            )}
                        </div>
                    ) : (
                        <button
                            onClick={onStart}
                            disabled={isLoading}
                            className="flex gap-3 justify-center items-center px-8 py-4 w-full text-lg font-bold text-white bg-indigo-600 rounded-xl transition-all shadow-lg hover:bg-indigo-700 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Play className="w-6 h-6 fill-current" />
                            <span>{overallStatus === 'completed' ? t('learning.lessonOverview.review') : hasStartedLearning ? t('learning.lessonOverview.continueLearning') : t('learning.lessonOverview.startLearning')}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Component Cards Grid */}
            <div className={cn(
                "grid gap-5",
                enabledComponents.length === 2 ? "md:grid-cols-2" :
                enabledComponents.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-1"
            )}>
                {enabledComponents.map((component, idx) => (
                    <ComponentCard
                        key={`${component.type}-${component.location}`}
                        component={component}
                        isLocked={isComponentLocked(idx) || canStartLearning === false}
                        onStart={onStartComponent && canStartLearning !== false
                            ? () => onStartComponent(component.type, component.location)
                            : undefined
                        }
                    />
                ))}
            </div>
        </div>
    )
}

// Component Card Sub-component
interface ComponentCardProps {
    component: ComponentDetail
    isLocked?: boolean
    onStart?: () => void
}

function ComponentCard({ component, isLocked = false, onStart }: ComponentCardProps) {
    const { t } = useTranslation('learner')
    const { location, required, progress } = component
    const config = getComponentConfig(component)
    if (!config) return null

    const Icon = config.icon
    const colorClass = config.colorClass
    const status = progress?.status || 'not_started'
    const isCompleted = status === 'completed'
    const progressText = formatProgressText(component, t)

    // Color classes based on type
    const getBgClass = () => {
        if (isLocked) return 'bg-gray-200 text-gray-400'
        const colorMap: Record<string, string> = {
            indigo: 'bg-indigo-50 text-indigo-600',
            green: 'bg-green-50 text-green-600',
            amber: 'bg-amber-50 text-amber-600',
            purple: 'bg-purple-50 text-purple-600',
            blue: 'bg-blue-50 text-blue-600',
        }
        return colorMap[colorClass] || colorMap.blue
    }

    const getBtnClass = () => {
        if (isLocked) return 'bg-gray-300 text-gray-500 cursor-not-allowed'
        const colorMap: Record<string, string> = {
            indigo: 'bg-indigo-600 hover:bg-indigo-700 text-white',
            green: 'bg-green-600 hover:bg-green-700 text-white',
            amber: 'bg-amber-600 hover:bg-amber-700 text-white',
            purple: 'bg-purple-600 hover:bg-purple-700 text-white',
            blue: 'bg-blue-600 hover:bg-blue-700 text-white',
        }
        return colorMap[colorClass] || colorMap.blue
    }

    const handleClick = () => {
        if (isLocked) {
            toast.warning(t('learning.lessonOverview.completePrevStep'))
            return
        }
        onStart?.()
    }

    return (
        <div className={cn(
            "flex relative flex-col gap-4 p-6 rounded-2xl border transition-all duration-200",
            isLocked ? "bg-gray-50 border-gray-200 opacity-60" : "bg-white border-gray-200"
        )}>
            {/* Icon & Title */}
            <div className="flex flex-col gap-4 items-center text-center">
                <div className={cn(
                    "flex relative justify-center items-center w-16 h-16 rounded-2xl",
                    getBgClass()
                )}>
                    {isLocked ? (
                        <Lock className="w-8 h-8" />
                    ) : (
                        <Icon className="w-8 h-8" />
                    )}
                    {isCompleted && !isLocked && (
                        <div className="flex absolute -top-1 -right-1 justify-center items-center w-6 h-6 bg-green-500 rounded-full">
                            <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                    )}
                </div>

                <div className="w-full">
                    <h3 className={cn(
                        "mb-1 text-lg font-bold",
                        isLocked ? "text-gray-400" : "text-gray-900"
                    )}>
                        {t(config.titleKey)}
                        {required && !isCompleted && (
                            <span className="ml-1 text-xs text-red-400">*</span>
                        )}
                    </h3>

                    {isLocked ? (
                        <p className="text-sm text-gray-400">{t('learning.lessonOverview.prevStepRequired')}</p>
                    ) : isCompleted ? (
                        <div className="space-y-1">
                            <p className="text-sm font-semibold text-green-600">{t('learning.lessonOverview.completed')}</p>
                            {progressText && (
                                <p className="text-xs text-gray-500">{progressText}</p>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">{t(config.descriptionKey)}</p>
                    )}
                </div>
            </div>

            {/* Action Button */}
            {onStart && (
                <div className="flex flex-col gap-2 mt-auto">
                    <button
                        onClick={handleClick}
                        disabled={isLocked}
                        className={cn(
                            "px-4 py-2 w-full font-medium rounded-lg transition-colors",
                            getBtnClass()
                        )}
                    >
                        {isCompleted ? t('learning.lessonOverview.doAgain') : t('learning.lessonOverview.start')}
                    </button>
                </div>
            )}
        </div>
    )
}
